import React, { createRef } from "react";
import { Button, CloseIconThin, Tooltip } from "@cmsgov/ds-medicare-gov";
import classNames from "classnames";
import uniqueid from "lodash.uniqueid";
// Import `IntlContext` directly from its source file or many tests will fail
import { IntlContext } from "../helpers/intlHooks";
import messages from "../translations/en-US.json";

interface TooltipWithCloseProps
  extends Omit<
    React.ComponentPropsWithRef<typeof Tooltip>,
    "placement" | "ref" | "id" | "dialog"
  > {
  /**
   * `headerText` is an optional heading for the tooltip's content. The required
   * `children` prop is used for the tooltip's trigger content (by default) and
   *  also the tooltip heading, unless `headerText` is also set.
   * If `headerText` is passed an empty string, no header will be displayed
   */
  headerText?: React.ReactNode;
  /**
   * `tagName` is an optional string representing an HTML element name. It
   * can be used to change the element that wraps the trigger of the
   * `Tooltip` component to create the custom `TooltipWithClose`.
   * The default is a `span`.
   */
  tagName?: keyof JSX.IntrinsicElements;
}

type ContentBuilderProps = Pick<
  TooltipWithCloseProps,
  "title" | "children" | "headerText"
>;

type currRefType = Tooltip &
  HTMLButtonElement & {
    handleClickOutside: (e: MouseEvent) => void;
    state: { active: boolean };
    triggerElement: HTMLElement;
  };

/**
 * Customized tooltip to match previous MPF implementation but leverage core DS
 * component functionality.
 * - Adds internal internationalization capability
 * - Wraps the original `title` prop (tooltip content) in markup that matches MPF's
 *   existing implementation
 * - Uses as a prefix for the tooltip trigger `aria-label`, in this order:
 *     - the optional `ariaLabel` prop, which is a string
 *     - the optional `headerText` prop, if it's a string
 *     - `children`, if it's a string
 * - NB: The `title` prop is for dialog **content**
 *
 * For additional prop and usage documentation:
 * @see https://github.cms.gov/pages/MedicareGov/mgov-design-system/components/tooltip/
 * @see https://design.cms.gov/components/tooltip/
 */
export class TooltipWithClose extends React.PureComponent<
  TooltipWithCloseProps
> {
  private tooltipRef = createRef<Tooltip & HTMLButtonElement>();
  private uniqueId = uniqueid();
  static contextType = IntlContext;
  context!: React.ContextType<typeof IntlContext>;

  constructor(props: TooltipWithCloseProps) {
    super(props);
    this.getCurrRef = this.getCurrRef.bind(this);
    this.onCloseButtonClickHandler = this.onCloseButtonClickHandler.bind(this);
    this.contentBuilder = this.contentBuilder.bind(this);
  }

  componentDidMount(): void {
    const currRef = this.getCurrRef();
    document.addEventListener("touchmove", () => {
      if (currRef && currRef?.state?.active === true) {
        currRef.handleClickOutside(new MouseEvent("click"));
      }
    });
  }

  getCurrRef(): currRefType | null {
    const currRef = this.tooltipRef.current as currRefType;
    return currRef;
  }

  onCloseButtonClickHandler(): void {
    const currRef = this.getCurrRef();
    if (currRef && currRef.triggerElement && "handleClickOutside" in currRef) {
      currRef.handleClickOutside(new MouseEvent("click"));
      currRef.triggerElement.focus();
    }
  }

  contentBuilder({
    title,
    headerText,
    children,
  }: ContentBuilderProps): JSX.Element {
    const closeButtonId = `mct-c-tooltip__close-button--${this.uniqueId}`;
    const fm = this.context?.formatMessage;
    const ariaLabel = fm ? fm({ id: "close" }) : "close";
    return (
      <>
        <div className="Tooltip__header">
          {/*
           * If `headerText` is provided, and is not an empty string, use it,
           * otherwise, use what's provided by children. If `headerText` is an
           * empty string, don't render a heading for the tooltip.
           */}
          {headerText === "" ? (
            <span></span>
          ) : (
            <h2 className="ds-text-heading--lg">{headerText || children}</h2>
          )}
          <Button
            variation="transparent"
            size="small"
            className="Tooltip__button--close"
            id={closeButtonId}
            onClick={this.onCloseButtonClickHandler}
            aria-label={ariaLabel}
          >
            <CloseIconThin />
          </Button>
        </div>
        <div className="Tooltip__body" data-testid="Tooltip__body">
          {title}
        </div>
      </>
    );
  }

  render(): JSX.Element {
    const {
      title,
      ariaLabel,
      headerText,
      children,
      className,
      activeClassName,
      maxWidth = "400px",
      tagName = "span",
      ...remainingProps
    } = this.props;
    const WrappingComponent = tagName;
    const fm = this.context?.formatMessage;
    const id = `mct-c-tooltip--${this.uniqueId}`;
    const triggerIdClassName = `mct-c-tooltip__trigger--${this.uniqueId}`;
    //Get a string prefix to describe the tooltip trigger to screen readers
    let ariaLabelPrefix = "";
    if (ariaLabel) {
      //ariaLabel` is optional and a `string`
      ariaLabelPrefix = `${ariaLabel}: `;
    } else if (headerText && typeof headerText === "string") {
      //`headerText` is optional and a `ReactNode`
      ariaLabelPrefix = `${headerText}: `;
    } else if (typeof children === "string") {
      //`children` is requred and a `ReactNode`
      ariaLabelPrefix = `${children}: `;
    }
    const content = this.contentBuilder({
      title,
      headerText,
      children,
    });
    const ariaLabelContent = fm
      ? `${ariaLabelPrefix}${fm({
          id: "tooltip_trigger_aria_label",
        })}`
      : `${ariaLabelPrefix}${messages.tooltip_trigger_aria_label}`;
    return (
      <WrappingComponent className="Tooltip Tooltip--with-close">
        <Tooltip
          title={content}
          ref={this.tooltipRef}
          id={id}
          dialog={true}
          className={classNames(
            className,
            triggerIdClassName,
            "Tooltip__trigger ds-c-button"
          )}
          activeClassName={classNames(
            activeClassName,
            "Tooltip__trigger--open"
          )}
          ariaLabel={ariaLabelContent}
          // The `Tooltip` component mysteriously omits many options for the `placement`
          // option of Popper JS. In this case, `Tooltip` will pass on any valid Popper value
          // @see https://popper.js.org/docs/v2/constructors/#options
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          placement="top-start"
          maxWidth={maxWidth}
          {...remainingProps}
        >
          {children}
        </Tooltip>
      </WrappingComponent>
    );
  }
}
